import React, { useState } from "react";
import { createPortal } from "react-dom";
import useBackdropClose from "../../hooks/useBackdropClose";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL } from "../../config/constants";
import { emitFormSaved } from "../../formEvents";
import {
  Overlay, Title, Period, RadioInput, RadioMark, CancelButton, SaveButton,
} from "../weeklyFormModal/styles";
import {
  Modal, Header, CloseButton, Questions, Question, QuestionLabel, ScaleOptions, ScaleOption, ScaleValue,
  ActionRow, ConfirmModal, ConfirmTitle, ConfirmBody, ReviewButton, ConfirmButton,
} from "./styles";

// Mismo número de preguntas y escala que valida el backend (POST /me/monthly-forms/:formType).
const MONTHLY_FORMS = {
  sus: { questions: 10, scale: 5 },
  aim: { questions: 4, scale: 5 },
  iam: { questions: 4, scale: 5 },
  fim: { questions: 4, scale: 5 },
};

const range = (length) => Array.from({ length }, (_, i) => i + 1);

// 'YYYY-MM-DD' → 'DD/MM/YYYY' sin pasar por Date, para evitar desfases de zona horaria.
const fmtDate = (ymd) => ymd.split("-").reverse().join("/");

const SubmitConfirmation = ({ loading, onReview, onConfirm }) => {
  const { t } = useTranslation();

  return (
    <Overlay {...useBackdropClose(onReview)}>
      <ConfirmModal onClick={(e) => e.stopPropagation()}>
        <Header>
          <ConfirmTitle>{t("monthlyForm.confirmTitle")}</ConfirmTitle>
          <CloseButton type="button" aria-label={t("monthlyForm.close")} onClick={onReview}>×</CloseButton>
        </Header>
        <ConfirmBody>{t("monthlyForm.confirmBody")}</ConfirmBody>
        <ActionRow>
          <ReviewButton type="button" onClick={onReview}>{t("monthlyForm.reviewAnswers")}</ReviewButton>
          <ConfirmButton type="button" disabled={loading} onClick={onConfirm}>
            {loading ? t("monthlyForm.saving") : t("monthlyForm.submit")}
          </ConfirmButton>
        </ActionRow>
      </ConfirmModal>
    </Overlay>
  );
};

/**
 * Rellena un formulario mensual de escala. `state` viene de GET /me/pending-forms.
 * Una vez enviado no se puede editar, por eso pide confirmación antes de mandarlo.
 */
const MonthlyFormModal = ({ formType, state, onClose, onSaved }) => {
  const { t } = useTranslation();
  const { questions, scale } = MONTHLY_FORMS[formType];
  const [answers, setAnswers] = useState({}); // número de pregunta → valor elegido
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);

  const complete = range(questions).every((question) => answers[question]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (complete) setConfirming(true);
  };

  const handleConfirm = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/me/monthly-forms/${formType}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          period_number: state.period_number,
          answers: range(questions).map((question) => answers[question]),
        }),
      });

      // El periodo ha cambiado con el modal abierto, o ya se envió desde otra pestaña.
      if (res.status === 409) {
        toast.error(t("monthlyForm.toastClosed"));
        emitFormSaved({ type: formType });
        onSaved();
        return;
      }
      if (!res.ok) throw new Error();

      toast.success(t("monthlyForm.toastSubmitted"));
      emitFormSaved({ type: formType });
      onSaved();
    } catch {
      toast.error(t("monthlyForm.toastError"));
      setLoading(false);
    }
  };

  // Portal a <body>: desde la barra lateral (position: fixed + z-index) el
  // overlay quedaría atrapado en su contexto de apilamiento, bajo la top bar.
  return createPortal(
    <>
      <Overlay {...useBackdropClose(onClose)}>
        <Modal onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
          <Header>
            <div>
              <Title>{t(`monthlyForm.${formType}.title`)}</Title>
              <Period>
                {t("monthlyForm.period", {
                  start: fmtDate(state.period_start_date),
                  end: fmtDate(state.period_end_date),
                })}
              </Period>
            </div>
            <CloseButton type="button" aria-label={t("monthlyForm.close")} onClick={onClose}>×</CloseButton>
          </Header>

          <Questions>
            {range(questions).map((question) => (
              <Question key={question}>
                <QuestionLabel>{question}. {t(`monthlyForm.${formType}.questions.q${question}`)}</QuestionLabel>
                <ScaleOptions $columns={scale}>
                  {range(scale).map((value) => (
                    <ScaleOption key={value}>
                      <RadioInput
                        type="radio"
                        name={`q${question}`}
                        value={value}
                        checked={answers[question] === value}
                        onChange={() => setAnswers((prev) => ({ ...prev, [question]: value }))}
                      />
                      <RadioMark />
                      <ScaleValue>{value}</ScaleValue>
                      {t(`monthlyForm.${formType}.scale.${value}`)}
                    </ScaleOption>
                  ))}
                </ScaleOptions>
              </Question>
            ))}
          </Questions>

          <ActionRow>
            <CancelButton type="button" onClick={onClose}>{t("monthlyForm.cancel")}</CancelButton>
            <SaveButton type="submit" disabled={!complete}>{t("monthlyForm.submit")}</SaveButton>
          </ActionRow>
        </Modal>
      </Overlay>

      {confirming && (
        <SubmitConfirmation
          loading={loading}
          onReview={() => { if (!loading) setConfirming(false); }}
          onConfirm={handleConfirm}
        />
      )}
    </>,
    document.body
  );
};

export default MonthlyFormModal;
